import type { BuildingDef, BuildingId } from '../types';

/**
 * Things the tribe raises. Bought with materials on a geometric cost curve,
 * kept for the length of a generation and lost on reset.
 */
export const BUILDINGS: BuildingDef[] = [
  // --- Age I ---------------------------------------------------------------
  {
    id: 'forageHeap',
    name: 'Forage Heap',
    plural: 'Forage Heaps',
    glyph: 'leaf',
    age: 0,
    description: 'A covered pile away from the wet. Holds more than hands can.',
    baseCost: { forage: 35 },
    costGrowth: 1.16,
    effects: [{ kind: 'storage', resource: 'forage', amount: 120 }],
  },
  {
    id: 'flintCache',
    name: 'Flint Cache',
    plural: 'Flint Caches',
    glyph: 'stone',
    age: 0,
    description: 'A marked hollow under a landmark stone.',
    baseCost: { flint: 25 },
    costGrowth: 1.16,
    effects: [{ kind: 'storage', resource: 'flint', amount: 90 }],
  },
  {
    id: 'tinderBundle',
    name: 'Tinder Bundle',
    plural: 'Tinder Bundles',
    glyph: 'grass',
    age: 0,
    description: 'Bound, hung, and turned so the damp never settles.',
    baseCost: { tinder: 25 },
    costGrowth: 1.16,
    effects: [{ kind: 'storage', resource: 'tinder', amount: 90 }],
  },
  {
    id: 'gatheringCircle',
    name: 'Gathering Circle',
    plural: 'Gathering Circles',
    glyph: 'tribe',
    age: 0,
    description: 'Ground cleared where the tribe sorts the day’s take together.',
    baseCost: { forage: 90, flint: 35 },
    costGrowth: 1.3,
    effects: [
      { kind: 'jobMult', job: 'forager', amount: 0.08 },
      { kind: 'jobMult', job: 'tinderer', amount: 0.08 },
    ],
  },
  {
    id: 'knappingFloor',
    name: 'Knapping Floor',
    plural: 'Knapping Floors',
    glyph: 'stone',
    age: 0,
    description: 'A worked patch scattered with flakes. Everyone learns faster near it.',
    baseCost: { flint: 70, forage: 40 },
    costGrowth: 1.3,
    effects: [{ kind: 'jobMult', job: 'knapper', amount: 0.12 }],
  },
  {
    id: 'windbreakScreen',
    name: 'Windbreak',
    plural: 'Windbreaks',
    glyph: 'home',
    age: 0,
    description: 'Brush piled against the weather. Barely a shelter, but people survive behind it.',
    baseCost: { forage: 45, tinder: 25 },
    costGrowth: 1.32,
    effects: [{ kind: 'shelter', amount: 2 }],
  },

  // --- Age II --------------------------------------------------------------
  {
    id: 'hearthRing',
    name: 'Hearth',
    plural: 'Hearths',
    glyph: 'flame',
    age: 1,
    description: 'A ring of stone that makes the fire a place rather than an event.',
    baseCost: { ember: 45, flint: 60 },
    costGrowth: 1.35,
    maxOwned: 12,
    requiresTech: 'hearth',
    effects: [
      { kind: 'nutritionMult', amount: 0.12 },
      { kind: 'shelter', amount: 1 },
    ],
  },
  {
    id: 'emberPit',
    name: 'Ember Pit',
    plural: 'Ember Pits',
    glyph: 'ember',
    age: 1,
    description: 'Banked deep and covered. The coal survives the night unattended.',
    baseCost: { ember: 40, tinder: 80 },
    costGrowth: 1.28,
    effects: [
      { kind: 'jobMult', job: 'firekeeper', amount: 0.11 },
      { kind: 'storage', resource: 'ember', amount: 60 },
    ],
  },
  {
    id: 'charPit',
    name: 'Charring Pit',
    plural: 'Charring Pits',
    glyph: 'coal',
    age: 1,
    description: 'A turfed-over burn that smoulders for days.',
    baseCost: { charcoal: 45, ember: 60 },
    costGrowth: 1.28,
    requiresTech: 'charringPit',
    effects: [
      { kind: 'jobMult', job: 'charburner', amount: 0.12 },
      { kind: 'storage', resource: 'charcoal', amount: 70 },
    ],
  },
  {
    id: 'smokeRack',
    name: 'Smoke Rack',
    plural: 'Smoke Racks',
    glyph: 'coal',
    age: 1,
    description: 'Food hung in the smoke keeps for a season instead of a day.',
    baseCost: { tinder: 120, charcoal: 50 },
    costGrowth: 1.3,
    requiresTech: 'smokeDrying',
    effects: [
      { kind: 'storageMult', resource: 'forage', amount: 0.25 },
      { kind: 'storageMult', resource: 'meat', amount: 0.35 },
    ],
  },

  // --- Age III -------------------------------------------------------------
  {
    id: 'spearRack',
    name: 'Spear Rack',
    plural: 'Spear Racks',
    glyph: 'spear',
    age: 2,
    description: 'Shafts kept straight and points kept sharp between hunts.',
    baseCost: { flint: 180, bone: 60 },
    costGrowth: 1.3,
    effects: [{ kind: 'jobMult', job: 'hunter', amount: 0.12 }],
  },
  {
    id: 'tanningFrame',
    name: 'Tanning Frame',
    plural: 'Tanning Frames',
    glyph: 'hide',
    age: 2,
    description: 'A hide stretched taut takes half the work to scrape.',
    baseCost: { bone: 90, hide: 60 },
    costGrowth: 1.3,
    effects: [{ kind: 'jobMult', job: 'tanner', amount: 0.13 }],
  },
  {
    id: 'meatPit',
    name: 'Cold Pit',
    plural: 'Cold Pits',
    glyph: 'deer',
    age: 2,
    description: 'Dug down to where the ground stays cold all summer.',
    baseCost: { meat: 120, flint: 100 },
    costGrowth: 1.24,
    effects: [
      { kind: 'storage', resource: 'meat', amount: 200 },
      { kind: 'storage', resource: 'hide', amount: 120 },
    ],
  },
  {
    id: 'boneCache',
    name: 'Bone Cache',
    plural: 'Bone Caches',
    glyph: 'bone',
    age: 2,
    description: 'Sorted by size and kind. The tribe’s first toolkit.',
    baseCost: { bone: 80, leather: 40 },
    costGrowth: 1.24,
    effects: [
      { kind: 'storage', resource: 'bone', amount: 150 },
      { kind: 'storage', resource: 'leather', amount: 120 },
    ],
  },
  {
    id: 'hideTent',
    name: 'Hide Tent',
    plural: 'Hide Tents',
    glyph: 'home',
    age: 2,
    description: 'Sewn skins over a bent-pole frame. It moves when the tribe moves.',
    baseCost: { leather: 90, bone: 70 },
    costGrowth: 1.42,
    requiresTech: 'boneNeedle',
    effects: [{ kind: 'shelter', amount: 4 }],
  },

  // --- Age IV --------------------------------------------------------------
  {
    id: 'hut',
    name: 'Hut',
    plural: 'Huts',
    glyph: 'home',
    age: 3,
    description: 'Poles, thatch and a floor dug a hand deep. It stays where it is put.',
    baseCost: { timber: 160, fiber: 90 },
    costGrowth: 1.38,
    requiresTech: 'windbreaks',
    effects: [{ kind: 'shelter', amount: 6 }],
  },
  {
    id: 'longhouseBuilding',
    name: 'Longhouse',
    plural: 'Longhouses',
    glyph: 'home',
    age: 3,
    description: 'One roof, many fires, forty people. The tribe becomes a single household.',
    baseCost: { timber: 700, fiber: 400, leather: 250 },
    costGrowth: 1.5,
    requiresTech: 'longhouse',
    effects: [
      { kind: 'shelter', amount: 22 },
      { kind: 'globalMult', amount: 0.03 },
    ],
  },
  {
    id: 'woodpile',
    name: 'Woodpile',
    plural: 'Woodpiles',
    glyph: 'tree',
    age: 3,
    description: 'Split, stacked and covered a full season before it is needed.',
    baseCost: { timber: 120 },
    costGrowth: 1.2,
    effects: [
      { kind: 'storage', resource: 'timber', amount: 260 },
      { kind: 'jobMult', job: 'woodcutter', amount: 0.06 },
    ],
  },
  {
    id: 'loomFrame',
    name: 'Loom Frame',
    plural: 'Loom Frames',
    glyph: 'thread',
    age: 3,
    description: 'Weighted warp threads on an upright frame. Cloth, at last.',
    baseCost: { timber: 200, fiber: 140 },
    costGrowth: 1.3,
    effects: [
      { kind: 'jobMult', job: 'weaver', amount: 0.13 },
      { kind: 'storage', resource: 'fiber', amount: 180 },
    ],
  },

  // --- Age V ---------------------------------------------------------------
  {
    id: 'ochreShrine',
    name: 'Ochre Grinding Stone',
    plural: 'Ochre Grinding Stones',
    glyph: 'hand',
    age: 4,
    description: 'A dished slab worn deeper every year by the same motion.',
    baseCost: { ochre: 150, timber: 300 },
    costGrowth: 1.3,
    effects: [
      { kind: 'jobMult', job: 'painter', amount: 0.13 },
      { kind: 'storage', resource: 'ochre', amount: 150 },
    ],
  },
  {
    id: 'storyStone',
    name: 'Painted Wall',
    plural: 'Painted Walls',
    glyph: 'spiral',
    age: 4,
    description: 'Knowledge put somewhere it cannot be forgotten or argued with.',
    baseCost: { ochre: 400, timber: 500 },
    costGrowth: 1.36,
    effects: [
      { kind: 'jobMult', job: 'shaman', amount: 0.16 },
      { kind: 'resourceMult', resource: 'insight', amount: 0.04 },
    ],
  },
  {
    id: 'ancestorPole',
    name: 'Ancestor Pole',
    plural: 'Ancestor Poles',
    glyph: 'skull',
    age: 4,
    description: 'Every keeper of the fire, carved in order, oldest at the top.',
    baseCost: { timber: 900, ochre: 500, leather: 300 },
    costGrowth: 1.55,
    maxOwned: 20,
    requiresTech: 'ancestorPoles',
    effects: [{ kind: 'akGainMult', amount: 0.04 }],
  },

  // --- Age VI --------------------------------------------------------------
  {
    id: 'field',
    name: 'Cleared Field',
    plural: 'Cleared Fields',
    glyph: 'grain',
    age: 5,
    description: 'Burned, stumped and turned. Ground the tribe has decided belongs to it.',
    baseCost: { timber: 1200, fiber: 600 },
    costGrowth: 1.32,
    effects: [
      { kind: 'jobMult', job: 'planter', amount: 0.13 },
      { kind: 'storage', resource: 'grain', amount: 400 },
    ],
  },
  {
    id: 'clayPit',
    name: 'Clay Pit',
    plural: 'Clay Pits',
    glyph: 'clay',
    age: 5,
    description: 'Cut back into the bank a little further every season.',
    baseCost: { timber: 900, flint: 1200 },
    costGrowth: 1.3,
    effects: [
      { kind: 'jobMult', job: 'digger', amount: 0.13 },
      { kind: 'storage', resource: 'clay', amount: 300 },
    ],
  },
  {
    id: 'kilnBuilding',
    name: 'Kiln',
    plural: 'Kilns',
    glyph: 'pot',
    age: 5,
    description: 'A chamber that holds heat long enough to change what mud is.',
    baseCost: { clay: 700, charcoal: 1500 },
    costGrowth: 1.34,
    requiresTech: 'kiln',
    effects: [
      { kind: 'jobMult', job: 'potter', amount: 0.15 },
      { kind: 'storage', resource: 'pottery', amount: 180 },
    ],
  },
  {
    id: 'granaryBuilding',
    name: 'Granary',
    plural: 'Granaries',
    glyph: 'home',
    age: 5,
    description: 'Raised on staddle stones. The surplus survives the winter and the rats.',
    baseCost: { timber: 2500, pottery: 250, clay: 1200 },
    costGrowth: 1.45,
    requiresTech: 'granary',
    effects: [
      { kind: 'storageMult', resource: '*', amount: 0.2 },
      { kind: 'shelter', amount: 8 },
    ],
  },
];

export const BUILDINGS_BY_ID: Record<BuildingId, BuildingDef> = Object.fromEntries(
  BUILDINGS.map((building) => [building.id, building]),
);

export const BUILDING_IDS: BuildingId[] = BUILDINGS.map((building) => building.id);

export function getBuilding(id: BuildingId): BuildingDef | undefined {
  return BUILDINGS_BY_ID[id];
}
